
import _ from 'lodash'
import { mapGetters } from 'vuex'
import { findDosageIndex } from '~/util/drugs'

const DEFAULT_FORM = {
    name: null,
    dosage: null,
    custom_dosage: null,
    package: null,
    custom_package: null,
    quantity: null,
    frequency: null,
    is_generic: null,
}
export default {
    props: {
        value: {
            type: Object,
            default: null
        },
        cancelLabel: {
            type: [String, Boolean],
            default: 'Cancel'
        },
        okLabel: {
            type: [String, Boolean],
            default: 'Done'
        },
        addWarning: {
            type: String,
            default: null
        },
    },
    data () {
        return {
            loading: false,
            watchers: [],
            dosages: [],
            form: {
                ...DEFAULT_FORM
            }
        }
    },
    fetch () {
        return this.onValue()
    },
    computed: {
        ...mapGetters(['year', 'frequencies', 'parseDosage', 'parsePackage', 'groupSlug']),
        packages () {
            return this.dosage?.packages || []
        },
        // A row is hand-typed only when there is no rxcui to fetch dosage
        // options for. custom_dosage does NOT mean manual any more: the checkup
        // sets it on every drug it adds, structured ones included, as a derived
        // display label (MedDetailPanel.vue -- "[strength, dosing label,
        // days-supply label]"). Gating on it rendered every drug a client
        // entered as a free-text row and hid the stored dosage and package
        // behind that label, so an agent could only reach the selects by
        // re-adding the drug and taking the duplicate warning. All 294 genuinely
        // manual rows in production have a null rxcui, and none of the
        // structured ones do, so the rxcui is the clean discriminator.
        isManual () {
            return !this.value?.name || !this.form?.rxcui || !!this.form?.custom_package
        },
        // Each option's value is the dosage's index in the list, not its
        // rxcui: two dosages can share one (#664), and duplicate <option>
        // values make the select emit a value it already holds — Vue then sees
        // no change and never repaints, while every rxcui lookup resolves to
        // the first of the colliding rows. That silently saved the wrong
        // strength and NDC, and priced the wrong drug in Oracle and the CRM.
        dosageOptions () {
            return this.dosages.map((dsg, index) => ({ value: index, text: dsg.text }))
        },
        dosageIndex () {
            const index = findDosageIndex(this.dosages, this.form?.dosage)
            return index > -1 ? index : null
        },
        dosage () {
            return this.dosages[this.dosageIndex] || null
        },
        quantityWarning () {
            const quantity = Number(this.form?.quantity)
            if (!_.isFinite(quantity) || quantity <= 0) {
                return null
            }

            const frequencyDays = this.getFrequencyDays(this.form?.frequency)
            if (!frequencyDays) {
                return null
            }

            const expectedQuantity = this.getExpectedQuantityForFrequency(frequencyDays)
            if (!_.isFinite(expectedQuantity) || expectedQuantity <= 0) {
                return null
            }

            const maxRecommendedQuantity = expectedQuantity * 1.05
            if (quantity <= maxRecommendedQuantity) {
                return null
            }

            const frequencyOption = _.find(this.frequencies, { value: this.form?.frequency })
            const frequencyText = frequencyOption?.text?.toLowerCase() || 'for this frequency'

            return `Entered quantity (${quantity}) is more than 5% above the default amount (${Math.round(expectedQuantity)}) ${frequencyText}.`
        },
    },
    mounted () {
        this.setGlobal('$drugForm', this)
    },
    methods: {
        selectDosage (index) {
            // 0 is a valid index, so test for null rather than truthiness.
            const dosage = index == null ? null : this.dosages[index]
            if (dosage) {
                // Compare indexes, not the objects: the stored dosage came from
                // the database while this one has been through parseDosage (it
                // carries `text` and `packages`), so a deep compare reports a
                // change even when the agent reselects the value already set.
                if (index !== this.dosageIndex) {
                    this.dropStaleClientLabel()
                }
                this.$set(this.form, 'dosage', dosage)
            }
        },
        // The client's label asserts a strength and a days supply ("23.75-95 mg
        // -- Twice daily -- 90-day fills"), so changing the dosage or the
        // frequency makes it untrue of the row. Drop it rather than leave the
        // display contradicting what is actually stored and priced. Quantity and
        // package changes leave it accurate, so they keep it.
        dropStaleClientLabel () {
            if (this.form?.custom_dosage) {
                this.$set(this.form, 'custom_dosage', null)
            }
        },
        getFrequencyDays (frequency) {
            const frequencyDaysByValue = {
                FREQUENCY_30_DAYS: 30,
                FREQUENCY_60_DAYS: 60,
                FREQUENCY_90_DAYS: 90,
                FREQUENCY_180_DAYS: 180,
                FREQUENCY_360_DAYS: 360,
            }
            return frequencyDaysByValue[frequency] || null
        },
        getExpectedQuantityForFrequency (frequencyDays) {
            const package30 = Number(_.get(this.form, 'package.default_quantity_30'))
            const package90 = Number(_.get(this.form, 'package.default_quantity_90'))

            const candidates = []
            if (_.isFinite(package30) && package30 > 0) {
                candidates.push(package30 * frequencyDays / 30)
            }
            if (_.isFinite(package90) && package90 > 0) {
                candidates.push(package90 * frequencyDays / 90)
            }

            if (!candidates.length) {
                return null
            }

            return _.max(candidates)
        },
        async ok () {
            const valid = await this.$validator.validate()
            if (valid) {
                this.$emit('input', this.form)
            }
        },
        cancel () {
            this.$emit('cancel', this.value)
        },
        async onValue () {
            this.watchers.forEach(unwatch => unwatch && unwatch())
            if (this.value) {
                this.form = _.cloneDeep(this.value)

                if (!this.form?.dosages?.length) {
                    if (!this.form.rxcui) {
                        this.dosages = []
                    } else {
                        this.loading = true
                        const { error, dosages } = await this.$try('getDrugOptions', { year: this.year, rxcui: this.form.rxcui, group_slug: this.groupSlug || this.$route?.params?.slug }, { spinner: false })
                        this.loading = false
                        if (!error) {
                            this.dosages = dosages.map(dsg => this.parseDosage(dsg))
                        }
                    }
                } else {
                    this.dosages = this.form.dosages
                }
                this.setDefaults()
                this.watchers.push(this.$watch('value', () => {
                    if (!_.isEqual(this.value, this.form)) {
                        this.onValue()
                    }
                }))
                this.watchers.push(this.$watch('form.dosage', () => {
                    this.setDefaults({ forcePackage0: true })
                }))
            } else {
                this.form = { ...DEFAULT_FORM }
            }
        },
        setDefaults (options = {}) {
            /* eslint-disable */
            if (!this.form.dosage) {
                this.$set(this.form, 'dosage', this.dosages[0])
            } else if (this.dosage) {
                const dsg = this.parseDosage(this.dosage, this.packages)
                this.$set(this.form, 'dosage', dsg)
            }
            if (!this.form.package || options.forcePackage0) {
                this.$set(this.form, 'package', this.packages[0])
            } else if (this.form.package) {
                const pkg = this.parsePackage(this.form.package, this.dosage)
                this.$set(this.form, 'package', pkg)
            }
            if (!this.form.quantity) {
                // Agents get the package's Oracle default prefilled; clients
                // enter their own. The `&& 0` that used to sit on this
                // condition made it dead, so agents had to retype a quantity
                // on every add or the form failed its required-field check.
                this.$set(this.form, 'quantity', this.canAgent ? (_.get(this.form, 'package.default_quantity_30') || 1) : null)
            }
            if (!this.form.frequency) {
                this.$set(this.form, 'frequency', 'FREQUENCY_30_DAYS')
            }
        },
    },
}
